// Résolution des informations du client d'un rendez-vous, d'un paiement ou
// d'un abonnement, que le client soit inscrit ou non.
//
// Ordre de priorité :
//   1. client inscrit (entity.client ou entity.user)
//   2. client inscrit de l'entité liée (rendez-vous ou abonnement du paiement)
//   3. données d'un client non inscrit (anonymous_data)
//   4. données saisies sur le paiement (extraData), sous ses deux formes :
//      { name, email, telephone } (réservation / souscription)
//      { nom, prenom, email, tel } (vente en boutique "pour quelqu'un d'autre")

const EMPTY_CLIENT = {
  name: "",
  email: "",
  telephone: "",
  whatsapp: "",
  isRegistered: false,
  isEmpty: true,
};

const text = (value) => (typeof value === "string" ? value.trim() : "");

const fromUser = (user) => {
  if (!user || typeof user !== "object") return null;
  const name = `${text(user.prenom)} ${text(user.nom)}`.trim();
  if (!name && !user.email && !user.telephone) return null;
  return {
    name,
    email: text(user.email),
    telephone: text(user.telephone),
    whatsapp: text(user.whatsapp) || text(user.telephone),
    isRegistered: true,
    isEmpty: false,
  };
};

const fromContactData = (data) => {
  if (!data || typeof data !== "object") return null;
  const name =
    text(data.name) || `${text(data.prenom)} ${text(data.nom)}`.trim();
  const telephone = text(data.telephone) || text(data.tel);
  const email = text(data.email);
  if (!name && !email && !telephone) return null;
  return {
    name,
    email,
    telephone,
    whatsapp: text(data.whatsapp) || telephone,
    isRegistered: false,
    isEmpty: false,
  };
};

/**
 * Renvoie { name, email, telephone, whatsapp, isRegistered, isEmpty }.
 */
export const getClientInfo = (entity) => {
  if (!entity) return EMPTY_CLIENT;
  return (
    fromUser(entity.client) ||
    fromUser(entity.user) ||
    fromUser(entity.appointment?.client) ||
    fromUser(entity.subscription?.client) ||
    fromContactData(entity.anonymous_data) ||
    fromContactData(entity.extraData) ||
    fromContactData(entity.appointment?.anonymous_data) ||
    fromContactData(entity.subscription?.anonymous_data) ||
    EMPTY_CLIENT
  );
};

/** Même résultat, ou null si aucune information client n'est connue. */
export const getClientContact = (entity) => {
  const info = getClientInfo(entity);
  return info.isEmpty ? null : info;
};

/** Nom à afficher, avec un libellé de repli si aucun nom n'est connu. */
export const getClientName = (entity, fallback = "Client non renseigné") =>
  getClientInfo(entity).name || fallback;

/** Lien WhatsApp (chiffres uniquement, comme l'exige wa.me). */
export const getWhatsappLink = (info) => {
  const digits = (info?.whatsapp || "").replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : "";
};

/** Lien d'appel téléphonique. */
export const getPhoneLink = (info) => {
  const phone = (info?.telephone || "").replace(/\s+/g, "");
  return phone ? `tel:${phone}` : "";
};

/** Recherche texte sur le nom, l'email et le téléphone du client. */
export const matchesClientSearch = (entity, term) => {
  const search = text(term).toLowerCase();
  if (!search) return true;
  const info = getClientInfo(entity);
  return (
    info.name.toLowerCase().includes(search) ||
    info.email.toLowerCase().includes(search) ||
    info.telephone.replace(/\s+/g, "").includes(search.replace(/\s+/g, ""))
  );
};
